// frontend/src/components/Dashboard/AnalyticsCards.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faUsers,
  faShoppingCart,
  faDollarSign,
  faPercentage,
  faEye,
  faClock,
  faArrowUp,
  faArrowDown,
  faTruck,
  faBox,
  faStar,
  faTractor,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faSpinner,
  faRocket,
  faBoxes,
  faWallet,
  faRepeat,
  faUserPlus,
  faCalendarAlt,
  faWeightScale,
  faShip,
  faWarehouse,
  faClipboardList,
  faExclamationTriangle,
  faCheckDouble,
  faLongArrowAltUp,
  faLongArrowAltDown,
  faGlobe,
  faBuilding,
  faAnchor,
} from "@fortawesome/free-solid-svg-icons";

// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    pending: "#f59e0b",
    processing: "#3b82f6",
    confirmed: "#8b5cf6",
    shipped: "#8b5cf6",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
    ordered: "#D9A321",
    in_transit: "#f59e0b",
    customs: "#ef4444",
    delayed: "#dc2626",
    completed: "#16a34a",
    failed: "#dc2626",
    paid: "#16a34a",
    unpaid: "#dc2626",
    processing_payment: "#f59e0b",
    failed_payment: "#dc2626",
    refunded_payment: "#6b7280",
    deposit_received: "#8b5cf6",
    ready_for_pickup: "#10b981",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusIcon = (status) => {
  const icons = {
    pending: faHourglassHalf,
    processing: faSpinner,
    confirmed: faCheckCircle,
    shipped: faRocket,
    out_for_delivery: faTruck,
    delivered: faCheckDouble,
    cancelled: faTimesCircle,
    refunded: faWallet,
    ordered: faClock,
    in_transit: faTruck,
    customs: faExclamationTriangle,
    delayed: faExclamationTriangle,
    completed: faCheckCircle,
    failed: faTimesCircle,
    paid: faDollarSign,
    unpaid: faTimesCircle,
    processing_payment: faSpinner,
    failed_payment: faTimesCircle,
    refunded_payment: faWallet,
    deposit_received: faDollarSign,
    ready_for_pickup: faCheckCircle,
  };
  return icons[status] || faBox;
};

// ============================================
// MAIN COMPONENT
// ============================================
const AnalyticsCards = ({ analytics, userRole, userType, isLoading }) => {
  const isAdmin = userRole === "admin" || userRole === "super_admin";
  const isFarmer = userType === "farmer";

  if (isLoading) {
    return (
      <div className="analytics-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading analytics...</p>
      </div>
    );
  }

  const safeAnalytics = {
    pendingOrders: analytics?.pendingOrders || 0,
    processingOrders: analytics?.processingOrders || 0,
    shippedOrders: analytics?.shippedOrders || 0,
    deliveredOrders: analytics?.deliveredOrders || 0,
    cancelledOrders: analytics?.cancelledOrders || 0,
    totalCustomers: analytics?.totalCustomers || 0,
    totalFarmers: analytics?.totalFarmers || 0,
    totalRevenue: analytics?.totalRevenue || 0,
    totalOrders: analytics?.totalOrders || 0,
    bulkOrders: analytics?.bulkOrders || 0,
    avgOrderValue: analytics?.avgOrderValue || 0,
    conversionRate: analytics?.conversionRate || 0,
    clv: analytics?.clv || 0,
    repeatRate: analytics?.repeatRate || 0,
    cartAbandonment: analytics?.cartAbandonment || 0,
    revenueChange: analytics?.revenueChange || "+0%",
    ordersChange: analytics?.ordersChange || "+0%",
    bulkOrdersChange: analytics?.bulkOrdersChange || "+0%",
    avgOrderChange: analytics?.avgOrderChange || "+$0",
    conversionChange: analytics?.conversionChange || "+0%",
    clvChange: analytics?.clvChange || "+$0",
    repeatChange: analytics?.repeatChange || "+0%",
    abandonmentChange: analytics?.abandonmentChange || "+0%",
    bulkMetrics: analytics?.bulkMetrics || {
      totalBulkRevenue: 0,
      totalBulkQuantity: 0,
      avgFarmerOrderValue: 0,
      activeFarmers: 0,
    },
    recentActivity: analytics?.recentActivity || [],
    malaysiaOrders: analytics?.malaysiaOrders || 0,
    malaysiaRevenue: analytics?.malaysiaRevenue || 0,
  };

  // Order Status Cards
  const orderStatusCards = [
    {
      title: "Pending Orders",
      value: safeAnalytics.pendingOrders,
      icon: faHourglassHalf,
      color: "#f59e0b",
      bgColor: "#fef3c7",
      description: "Awaiting processing",
    },
    {
      title: "Processing",
      value: safeAnalytics.processingOrders,
      icon: faSpinner,
      color: "#3b82f6",
      bgColor: "#dbeafe",
      description: "Being prepared",
    },
    {
      title: "Shipped",
      value: safeAnalytics.shippedOrders,
      icon: faRocket,
      color: "#8b5cf6",
      bgColor: "#ede9fe",
      description: "In transit",
    },
    {
      title: "Delivered",
      value: safeAnalytics.deliveredOrders,
      icon: faCheckCircle,
      color: "#16a34a",
      bgColor: "#dcfce7",
      description: "Successfully completed",
    },
    {
      title: "Cancelled",
      value: safeAnalytics.cancelledOrders,
      icon: faTimesCircle,
      color: "#dc2626",
      bgColor: "#fee2e2",
      description: "Order cancelled",
    },
  ];

  // Customer Types
  const customerTypes = [
    {
      label: "Total Customers",
      value: safeAnalytics.totalCustomers?.toLocaleString() || "0",
      icon: faUsers,
      color: "#3b82f6",
      bgColor: "#dbeafe",
      description: "All registered users",
    },
    {
      label: "Active Farmers",
      value: safeAnalytics.totalFarmers?.toLocaleString() || "0",
      icon: faTractor,
      color: "#10b981",
      bgColor: "#dcfce7",
      description: "Registered farmers",
    },
    {
      label: "Bulk Orders",
      value: safeAnalytics.bulkOrders?.toLocaleString() || "0",
      icon: faBoxes,
      color: "#8b5cf6",
      bgColor: "#ede9fe",
      description: "100kg+ orders",
    },
    {
      label: "Total Orders",
      value: safeAnalytics.totalOrders?.toLocaleString() || "0",
      icon: faShoppingCart,
      color: "#f59e0b",
      bgColor: "#fef3c7",
      description: "All-time orders",
    },
  ];

  // Main Metrics
  const mainCards = [
    {
      title: "Total Revenue",
      value: `$${safeAnalytics.totalRevenue.toLocaleString()}`,
      change: safeAnalytics.revenueChange,
      trend: safeAnalytics.revenueChange?.startsWith("+") ? "up" : "down",
      icon: faDollarSign,
      color: "#f59e0b",
      description: "Total sales revenue",
    },
    {
      title: "Total Orders",
      value: safeAnalytics.totalOrders.toLocaleString(),
      change: safeAnalytics.ordersChange,
      trend: safeAnalytics.ordersChange?.startsWith("+") ? "up" : "down",
      icon: faShoppingCart,
      color: "#ef4444",
      description: "All-time orders",
    },
    {
      title: "Bulk Orders",
      value: safeAnalytics.bulkOrders.toLocaleString(),
      change: safeAnalytics.bulkOrdersChange,
      trend: safeAnalytics.bulkOrdersChange?.startsWith("+") ? "up" : "down",
      icon: faBoxes,
      color: "#8b5cf6",
      description: "100kg+ orders",
    },
    {
      title: "Avg Order Value",
      value: `$${safeAnalytics.avgOrderValue.toLocaleString()}`,
      change: safeAnalytics.avgOrderChange,
      trend: safeAnalytics.avgOrderChange?.startsWith("+") ? "up" : "down",
      icon: faChartLine,
      color: "#10b981",
      description: "Average order amount",
    },
  ];

  // Performance Metrics
  const performanceCards = [
    {
      title: "Conversion Rate",
      value: `${safeAnalytics.conversionRate}%`,
      change: safeAnalytics.conversionChange,
      trend: safeAnalytics.conversionChange?.startsWith("+") ? "up" : "down",
      icon: faPercentage,
      color: "#10b981",
      description: "Visitors to customers",
    },
    {
      title: "Customer LTV",
      value: `$${safeAnalytics.clv.toLocaleString()}`,
      change: safeAnalytics.clvChange,
      trend: safeAnalytics.clvChange?.startsWith("+") ? "up" : "down",
      icon: faStar,
      color: "#8b5cf6",
      description: "Lifetime value per customer",
    },
    {
      title: "Repeat Purchase Rate",
      value: `${safeAnalytics.repeatRate}%`,
      change: safeAnalytics.repeatChange,
      trend: safeAnalytics.repeatChange?.startsWith("+") ? "up" : "down",
      icon: faRepeat,
      color: "#ec4899",
      description: "Customers who return",
    },
    {
      title: "Cart Abandonment",
      value: `${safeAnalytics.cartAbandonment}%`,
      change: safeAnalytics.abandonmentChange,
      trend: safeAnalytics.abandonmentChange?.startsWith("-") ? "down" : "up",
      icon: faEye,
      color: "#ef4444",
      description: "Abandoned carts rate",
    },
  ];

  // Order Status Section
  const OrderStatusSection = () => (
    <div className="order-status-section">
      <div className="section-header">
        <div className="section-title">
          <FontAwesomeIcon icon={faClipboardList} />
          <h3>Order Status Overview</h3>
        </div>
        <span className="section-badge">
          <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} Sourced
        </span>
      </div>
      <div className="order-status-grid">
        {orderStatusCards.map((card, index) => (
          <div
            key={index}
            className="order-status-card"
            style={{ background: card.bgColor, borderLeftColor: card.color }}
          >
            <div className="order-status-icon" style={{ color: card.color }}>
              <FontAwesomeIcon icon={card.icon} />
            </div>
            <div className="order-status-info">
              <div className="order-status-value">{card.value}</div>
              <div className="order-status-title">{card.title}</div>
              <div className="order-status-description">{card.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Customer Summary
  const CustomerSummarySection = () => (
    <div className="customer-summary">
      <div className="section-header">
        <div className="section-title">
          <FontAwesomeIcon icon={faUsers} />
          <h3>Account Overview</h3>
        </div>
        <span className="section-badge">
          <FontAwesomeIcon icon={faBuilding} /> {SUPPLIER.name}
        </span>
      </div>
      <div className="customer-summary-grid">
        {customerTypes.map((type, index) => (
          <div
            key={index}
            className="customer-summary-card"
            style={{ background: type.bgColor }}
          >
            <FontAwesomeIcon
              icon={type.icon}
              style={{ color: type.color, fontSize: "1.5rem" }}
            />
            <div className="customer-summary-value">{type.value}</div>
            <div className="customer-summary-label">{type.label}</div>
            <div className="customer-summary-description">
              {type.description}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Bulk Metrics Section
  const BulkMetricsSection = () => {
    const { bulkMetrics } = safeAnalytics;
    const metrics = [
      {
        label: "Bulk Revenue",
        value: `$${bulkMetrics.totalBulkRevenue?.toLocaleString() || "0"}`,
        icon: faDollarSign,
        color: "#f59e0b",
      },
      {
        label: "Bulk Quantity Sold",
        value: `${bulkMetrics.totalBulkQuantity?.toLocaleString() || "0"} kg`,
        icon: faWeightScale,
        color: "#10b981",
      },
      {
        label: "Avg Farmer Order",
        value: `$${bulkMetrics.avgFarmerOrderValue?.toLocaleString() || "0"}`,
        icon: faTractor,
        color: "#8b5cf6",
      },
      {
        label: "Active Farmers",
        value: bulkMetrics.activeFarmers?.toLocaleString() || "0",
        icon: faUsers,
        color: "#3b82f6",
      },
    ];

    return (
      <div className="bulk-metrics-section">
        <div className="section-header">
          <div className="section-title">
            <FontAwesomeIcon icon={faTractor} />
            <h3>🌾 Bulk & Farmer Metrics</h3>
          </div>
          <span className="section-badge">
            <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
          </span>
        </div>
        <div className="bulk-metrics-grid">
          {metrics.map((metric, index) => (
            <div key={index} className="bulk-metric-card">
              <div className="bulk-metric-icon" style={{ color: metric.color }}>
                <FontAwesomeIcon icon={metric.icon} />
              </div>
              <div className="bulk-metric-value">{metric.value}</div>
              <div className="bulk-metric-label">{metric.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Malaysia Origin Stats
  const MalaysiaStatsSection = () => (
    <div className="malaysia-stats-section">
      <div className="section-header">
        <div className="section-title">
          <FontAwesomeIcon icon={faShip} />
          <h3>🇲🇾 {SUPPLIER.country} Origin Stats</h3>
        </div>
        <span className="section-badge">
          <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country}
        </span>
      </div>
      <div className="malaysia-stats-grid">
        <div className="malaysia-stat-card">
          <div className="malaysia-stat-value">
            {safeAnalytics.malaysiaOrders || 0}
          </div>
          <div className="malaysia-stat-label">
            Orders from {SUPPLIER.country}
          </div>
        </div>
        <div className="malaysia-stat-card" style={{ borderColor: "#D9A321" }}>
          <div className="malaysia-stat-value" style={{ color: "#D9A321" }}>
            ${(safeAnalytics.malaysiaRevenue || 0).toLocaleString()}
          </div>
          <div className="malaysia-stat-label">
            Revenue from {SUPPLIER.country}
          </div>
        </div>
        <div className="malaysia-stat-card">
          <div className="malaysia-stat-value">{SUPPLIER.name}</div>
          <div className="malaysia-stat-label">Primary Supplier</div>
        </div>
      </div>
    </div>
  );

  // Recent Activity
  const RecentActivitySection = () => {
    const activities = safeAnalytics.recentActivity || [];

    if (activities.length === 0) {
      return (
        <div className="recent-activity-section">
          <div className="section-header">
            <div className="section-title">
              <FontAwesomeIcon icon={faClock} />
              <h3>Recent Order Activity</h3>
            </div>
          </div>
          <div className="activity-empty">
            <FontAwesomeIcon icon={faBox} size="2x" />
            <p>No recent activity to display</p>
          </div>
        </div>
      );
    }

    return (
      <div className="recent-activity-section">
        <div className="section-header">
          <div className="section-title">
            <FontAwesomeIcon icon={faClock} />
            <h3>Recent Order Activity</h3>
          </div>
          <span className="section-badge">
            <FontAwesomeIcon icon={faCalendarAlt} /> Last 7 days
          </span>
        </div>
        <div className="activity-timeline">
          {activities.slice(0, 10).map((activity, index) => (
            <div key={index} className="activity-item">
              <div
                className="activity-icon"
                style={{ color: getStatusColor(activity.status) }}
              >
                <FontAwesomeIcon icon={getStatusIcon(activity.status)} />
              </div>
              <div className="activity-details">
                <div className="activity-title">
                  Order #{activity.orderNumber || activity._id?.slice(-8)}
                  {activity.origin === SUPPLIER.country && (
                    <span className="origin-badge">
                      <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country}
                    </span>
                  )}
                </div>
                <div
                  className="activity-status"
                  style={{ color: getStatusColor(activity.status) }}
                >
                  {activity.status?.charAt(0).toUpperCase() +
                    activity.status?.slice(1) || "Unknown"}
                </div>
                <div className="activity-date">
                  {activity.createdAt
                    ? new Date(activity.createdAt).toLocaleDateString()
                    : "N/A"}
                  {activity.createdAt && (
                    <span className="activity-time">
                      {new Date(activity.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  )}
                </div>
              </div>
              <div className="activity-amount">
                $
                {(
                  activity.total?.usd ||
                  activity.totalAmount?.usd ||
                  0
                ).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="analytics-container">
      <OrderStatusSection />
      <CustomerSummarySection />

      {isAdmin && (
        <>
          <div className="analytics-cards-grid">
            {mainCards.map((card, index) => (
              <div key={index} className="analytics-card">
                <div className="analytics-card-header">
                  <div
                    className="analytics-card-icon"
                    style={{ background: `${card.color}15` }}
                  >
                    <FontAwesomeIcon
                      icon={card.icon}
                      style={{ color: card.color }}
                    />
                  </div>
                  <span className={`analytics-card-trend ${card.trend}`}>
                    <FontAwesomeIcon
                      icon={card.trend === "up" ? faArrowUp : faArrowDown}
                    />
                    {card.change}
                  </span>
                </div>
                <div className="analytics-card-value">{card.value}</div>
                <div className="analytics-card-title">{card.title}</div>
                <div className="analytics-card-description">
                  {card.description}
                </div>
              </div>
            ))}
          </div>

          <div className="analytics-cards-grid performance-grid">
            {performanceCards.map((card, index) => (
              <div key={index} className="analytics-card performance-card">
                <div className="analytics-card-header">
                  <div
                    className="analytics-card-icon"
                    style={{ background: `${card.color}15` }}
                  >
                    <FontAwesomeIcon
                      icon={card.icon}
                      style={{ color: card.color }}
                    />
                  </div>
                  <span className={`analytics-card-trend ${card.trend}`}>
                    <FontAwesomeIcon
                      icon={card.trend === "up" ? faArrowUp : faArrowDown}
                    />
                    {card.change}
                  </span>
                </div>
                <div className="analytics-card-value">{card.value}</div>
                <div className="analytics-card-title">{card.title}</div>
                <div className="analytics-card-description">
                  {card.description}
                </div>
              </div>
            ))}
          </div>

          <BulkMetricsSection />
          <MalaysiaStatsSection />
        </>
      )}

      <RecentActivitySection />

      <style>{`
        .analytics-container {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          padding: 1rem 0;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .section-title h3 {
          color: #006b2e;
          margin: 0;
          font-size: 1rem;
          font-family: "Montserrat", sans-serif;
        }

        .section-badge {
          font-size: 0.65rem;
          padding: 0.2rem 0.75rem;
          border-radius: 12px;
          background: #f0f0f0;
          color: #5a5a5a;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .order-status-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 0.75rem;
        }

        .order-status-card {
          padding: 1rem;
          border-radius: 8px;
          border-left: 3px solid;
          transition: all 0.3s ease;
        }

        .order-status-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .order-status-icon { font-size: 1.25rem; margin-bottom: 0.5rem; }
        .order-status-value { font-size: 1.5rem; font-weight: 700; color: #1f1f1f; font-family: "Montserrat", sans-serif; }
        .order-status-title { font-size: 0.75rem; color: #5a5a5a; font-weight: 500; }
        .order-status-description { font-size: 0.65rem; color: #9ca3af; margin-top: 0.125rem; }

        .customer-summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 0.75rem;
        }

        .customer-summary-card {
          padding: 1rem;
          border-radius: 8px;
          text-align: center;
          transition: all 0.3s ease;
        }

        .customer-summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .customer-summary-value { font-size: 1.5rem; font-weight: 700; color: #1f1f1f; margin: 0.25rem 0; font-family: "Montserrat", sans-serif; }
        .customer-summary-label { font-size: 0.75rem; color: #5a5a5a; font-weight: 500; }
        .customer-summary-description { font-size: 0.65rem; color: #9ca3af; }

        .analytics-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .analytics-card {
          background: #ffffff;
          padding: 1rem 1.25rem;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
          transition: all 0.3s ease;
        }

        .analytics-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .analytics-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .analytics-card-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .analytics-card-icon svg { font-size: 1rem; }

        .analytics-card-trend {
          font-size: 0.65rem;
          font-weight: 600;
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
        }

        .analytics-card-trend.up { background: #dcfce7; color: #16a34a; }
        .analytics-card-trend.down { background: #fee2e2; color: #dc2626; }

        .analytics-card-value { font-size: 1.5rem; font-weight: 700; color: #1f1f1f; font-family: "Montserrat", sans-serif; }
        .analytics-card-title { font-size: 0.75rem; color: #5a5a5a; font-weight: 500; }
        .analytics-card-description { font-size: 0.65rem; color: #9ca3af; margin-top: 0.125rem; }

        .bulk-metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
        }

        .bulk-metric-card {
          background: #ffffff;
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
          text-align: center;
          transition: all 0.3s ease;
        }

        .bulk-metric-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .bulk-metric-icon { font-size: 1.5rem; margin-bottom: 0.5rem; }
        .bulk-metric-value { font-size: 1.25rem; font-weight: 700; color: #1f1f1f; font-family: "Montserrat", sans-serif; }
        .bulk-metric-label { font-size: 0.75rem; color: #5a5a5a; }

        .malaysia-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
        }

        .malaysia-stat-card {
          background: #ffffff;
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
          text-align: center;
          transition: all 0.3s ease;
          border-left: 3px solid #006B2E;
        }

        .malaysia-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .malaysia-stat-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #006B2E;
          font-family: "Montserrat", sans-serif;
        }

        .malaysia-stat-label {
          font-size: 0.7rem;
          color: #5a5a5a;
          margin-top: 0.25rem;
        }

        .origin-badge {
          display: inline-block;
          margin-left: 0.5rem;
          font-size: 0.55rem;
          background: #dcfce7;
          color: #16a34a;
          padding: 0.1rem 0.4rem;
          border-radius: 8px;
        }

        .activity-timeline {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .activity-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem 1rem;
          background: #ffffff;
          border-radius: 8px;
          border: 1px solid #f0f0f0;
          transition: all 0.3s ease;
        }

        .activity-item:hover { background: #f8f9fa; }
        .activity-icon { font-size: 1.25rem; width: 36px; text-align: center; }

        .activity-details { flex: 1; }
        .activity-title { font-weight: 500; color: #1f1f1f; }
        .activity-status { font-size: 0.75rem; font-weight: 500; }
        .activity-date { font-size: 0.7rem; color: #9ca3af; }
        .activity-time { margin-left: 0.5rem; }
        .activity-amount { font-weight: 600; color: #d9a321; }

        .activity-empty {
          text-align: center;
          padding: 2rem;
          color: #9ca3af;
        }

        .activity-empty svg { margin-bottom: 0.5rem; opacity: 0.5; }

        .analytics-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          color: #5a5a5a;
        }

        .analytics-loading svg { color: #d9a321; margin-bottom: 1rem; }

        @media (max-width: 768px) {
          .order-status-grid { grid-template-columns: repeat(2, 1fr); }
          .customer-summary-grid { grid-template-columns: repeat(2, 1fr); }
          .analytics-cards-grid { grid-template-columns: 1fr 1fr; }
          .bulk-metrics-grid { grid-template-columns: 1fr 1fr; }
          .malaysia-stats-grid { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 480px) {
          .order-status-grid { grid-template-columns: 1fr; }
          .customer-summary-grid { grid-template-columns: 1fr; }
          .analytics-cards-grid { grid-template-columns: 1fr; }
          .bulk-metrics-grid { grid-template-columns: 1fr; }
          .malaysia-stats-grid { grid-template-columns: 1fr; }
          .section-header { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsCards;
